import React, { useState } from "react";
import Loader from "./loader/Loader";


export interface DeclineRecord {
  market_id: number;
  declined_by: string;
  decline_comment: string;
  declined_at: string;
}

export interface PendingMarket {
  id: number;
  question: string;
  category: string;
  image_url: string;
  resolved: boolean;
  suggested_by: string;
  isLive: boolean;
  status: number;
  outcomes_count: number;
  outcomes: Array<{ outcome_id: number; name: string }>;
  b: number;
  approved_by: string;
  expire: number;
  approvalCount: number;
  declineRecord: DeclineRecord | null;
}

interface MarketApprovalSectionProps {
  pendingMarkets: PendingMarket[];
  loadingPending: boolean;
  liquidityInputs: Record<number, string>;
  setLiquidityInputs: React.Dispatch<
    React.SetStateAction<Record<number, string>>
  >;
  declineInputs: Record<number, string>;
  setDeclineInputs: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  approvingId: number | null;
  decliningId: number | null;
  platformPoolBalance: {
    totalDeposited: number;
    available: number;
  };
  scale: number;
  computeUsdtCost: (bValue: number, outcomesCount: number) => number;
  onSetLiquidity: (marketId: number) => void;
  onDeclineMarket: (marketId: number) => void;
}

const MarketApprovalSection: React.FC<MarketApprovalSectionProps> = ({
  pendingMarkets,
  loadingPending,
  liquidityInputs,
  setLiquidityInputs,
  declineInputs,
  setDeclineInputs,
  approvingId,
  decliningId,
  platformPoolBalance,
  scale,
  computeUsdtCost,
  onSetLiquidity,
  onDeclineMarket,
}) => {
  const [activeTab, setActiveTab] = useState<"popular" | "all">("all");

const filteredMarkets =
  activeTab === "popular"
      ? [...pendingMarkets]
          .filter((market) => market.approvalCount > 0)
          .sort((a, b) => b.approvalCount - a.approvalCount)
      : pendingMarkets;

  return (
    <div className="admin-section">
      <h3>Market Approval</h3>
      <p className="section-description" style={{ color: "#3b82f6" }}>
        Approve or decline pending markets. Set liquidity (b) when approving.
        Only admin approval makes a market live. Community votes are shown as
        popularity indicators.
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 16px",
          marginBottom: "16px",
          borderRadius: "8px",
          backgroundColor: "rgba(16, 185, 129, 0.08)",
          border: "1px solid rgba(16, 185, 129, 0.25)",
        }}
      >
        <span style={{ fontSize: "0.9em", color: "#9ca3af" }}>
          Total Deposited:
        </span>
        <span
          style={{
            fontSize: "1.1em",
            fontWeight: "bold",
            color: "#10b981",
          }}
        >
          {platformPoolBalance.totalDeposited.toLocaleString(undefined, {
            minimumFractionDigits: 6,
            maximumFractionDigits: 6,
          })}{" "}
          USDT
        </span>
        <span style={{ fontSize: "0.9em", color: "#9ca3af" }}>
          Available:
        </span>
        <span
          style={{
            fontSize: "1.1em",
            fontWeight: "bold",
            color: "#10b981",
          }}
        >
          {platformPoolBalance.available.toLocaleString(undefined, {
            minimumFractionDigits: 6,
            maximumFractionDigits: 6,
          })}{" "}
          USDT
        </span>
      </div>

      {loadingPending ? (
        <Loader
          label="Loading pending markets..."
          className="app-loader-compact"
        />
      ) : pendingMarkets.length === 0 ? (
        <div className="no-markets">No pending markets found.</div>
      ) : (
      // Filter markets based on active tab
      <>
          <div className="admin-tabs">
            <button
              className={activeTab === "popular" ? "active" : ""}
              onClick={() => setActiveTab("popular")}
            >
              Most Popular
            </button>
            <button
              className={activeTab === "all" ? "active" : ""}
              onClick={() => setActiveTab("all")}
            >
              All Markets
            </button>
        </div>
        <div className="market-cards">
          {filteredMarkets.map((market) => {
            const bInput = liquidityInputs[market.id] || "";
            const bNum = parseFloat(bInput) || 0;
            const usdtCost = computeUsdtCost(bNum, market.outcomes_count);
            const communityVotes = market.approvalCount;
            const superAdminApproved =
              market.approved_by !== "" && market.approved_by !== "............1";
            const isDeclined =
              market.status === 0 && market.declineRecord != null;
            const bInRange = bNum >= 10 && bNum <= 1000;

            return (
              <div
                key={market.id}
                className="market-card"
                style={{
                  border: isDeclined
                    ? "1px solid #ef4444"
                    : superAdminApproved
                      ? "1px solid #3b82f6"
                      : "1px solid #374151",
                  backgroundColor: isDeclined
                    ? "rgba(239, 68, 68, 0.05)"
                    : superAdminApproved
                      ? "rgba(59, 130, 246, 0.05)"
                      : "transparent",
                  padding: "16px",
                  marginBottom: "16px",
                  borderRadius: "8px",
                }}
              >
                <div className="community-proposal-header">
                  <div className="proposal-id">
                      #{market.id}
                  </div>
                  <div className="community-vote-count" 
                  style={{
                    backgroundColor:
                        communityVotes > 0
                          ? "rgba(16, 185, 129, 0.2)"
                          : "rgba(107, 114, 128, 0.2)",
                    color: communityVotes > 0 ? "#10b981" : "#6b7280"
                  }}>
                    Community Interest: {communityVotes} vote
                    {communityVotes !== 1 ? "s" : ""}
                  </div>
                </div>
                <div
                  className="market-question"
                  style={{ fontWeight: "bold", marginBottom: "8px" }}
                >
                  {market.question}
                </div>
                <div
                  className="market-meta"
                  style={{
                    fontSize: "0.85em",
                    color: "#9ca3af",
                    marginBottom: "8px",
                  }}
                >
                  Category: {market.category} | Proposed by:{" "}
                  {market.suggested_by} | Type:{" "}
                  {market.outcomes_count === 2
                    ? "Binary"
                    : `${market.outcomes_count}-outcome`}
                </div>

                {market.outcomes && market.outcomes.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      flexWrap: "wrap",
                      marginBottom: "10px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.85em",
                        color: "#9ca3af",
                        fontWeight: 600,
                      }}
                    >
                      Outcomes:
                    </span>
                    {market.outcomes.map((outcome, index) => (
                      <span
                        key={index}
                        style={{
                          padding: "2px 10px",
                          borderRadius: "12px",
                          fontSize: "0.8em",
                          backgroundColor: "rgba(139, 92, 246, 0.15)",
                          color: "#a78bfa",
                        }}
                      >
                        {outcome.name}
                      </span>
                    ))}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "12px",
                  }}
                >
                  {/* <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "0.8em",
                      backgroundColor:
                        communityVotes > 0
                          ? "rgba(16, 185, 129, 0.2)"
                          : "rgba(107, 114, 128, 0.2)",
                      color: communityVotes > 0 ? "#10b981" : "#6b7280",
                    }}
                  >
                    Community Interest: {communityVotes} vote
                    {communityVotes !== 1 ? "s" : ""}
                  </span> */}
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "0.8em",
                      backgroundColor: superAdminApproved
                        ? "rgba(59, 130, 246, 0.2)"
                        : "rgba(107, 114, 128, 0.2)",
                      color: superAdminApproved ? "#3b82f6" : "#6b7280",
                    }}
                  >
                    {superAdminApproved
                      ? `Super Admin: ${market.approved_by}`
                      : "Super Admin: Pending"}
                  </span>
                  {isDeclined && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "0.8em",
                        backgroundColor: "rgba(239, 68, 68, 0.2)",
                        color: "#ef4444",
                      }}
                    >
                      Declined
                    </span>
                  )}
                  {market.b > 0 && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "0.8em",
                        backgroundColor: "rgba(59, 130, 246, 0.2)",
                        color: "#93c5fd",
                      }}
                    >
                      b = {(market.b / scale).toFixed(0)}
                    </span>
                  )}
                </div>

                {market.declineRecord && (
                  <div
                    style={{
                      padding: "10px",
                      marginBottom: "12px",
                      borderRadius: "6px",
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.85em",
                        color: "#ef4444",
                        fontWeight: "bold",
                        marginBottom: "4px",
                      }}
                    >
                      Declined by {market.declineRecord.declined_by}
                    </div>
                    <div style={{ fontSize: "0.9em", color: "#fca5a5" }}>
                      {market.declineRecord.decline_comment}
                    </div>
                    {market.declineRecord.declined_at && (
                      <div
                        style={{
                          fontSize: "0.75em",
                          color: "#9ca3af",
                          marginTop: "4px",
                        }}
                      >
                        {new Date(
                          `${market.declineRecord.declined_at}Z`,
                        ).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}

                <div
                  style={{
                    marginTop: "12px",
                    padding: "14px",
                    borderRadius: "8px",
                    backgroundColor: "rgba(30, 41, 59, 0.6)",
                    border: "1px solid #334155",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: "0.95em",
                      color: "#f8fafc",
                      letterSpacing: "0.03em",
                    }}
                  >
                    Action Required
                  </h4>

                  {isDeclined ? (
                    <div
                      style={{
                        padding: "12px",
                        marginBottom: "10px",
                        borderRadius: "6px",
                        backgroundColor: "rgba(239, 68, 68, 0.05)",
                        border: "1px solid rgba(239, 68, 68, 0.2)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "0.9em",
                          color: "#ef4444",
                          fontWeight: "bold",
                        }}
                      >
                        This market has been declined and cannot be approved.
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "12px",
                        marginBottom: "10px",
                        borderRadius: "6px",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontWeight: "bold",
                          marginBottom: "6px",
                          fontSize: "0.9em",
                          color: "#93c5fd",
                        }}
                      >
                        Set Liquidity (b) &amp; Approve
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <input
                          type="number"
                          placeholder="10 - 1000"
                          value={bInput}
                          onChange={(event) =>
                            setLiquidityInputs((current) => ({
                              ...current,
                              [market.id]: event.target.value,
                            }))
                          }
                          style={{
                            width: "120px",
                            padding: "8px",
                            borderRadius: "6px",
                            backgroundColor: "#1f2937",
                            color: "#e5e7eb",
                            border: "1px solid #374151",
                          }}
                          min={10}
                          max={1000}
                          step="any"
                        />
                        <span style={{ fontSize: "0.85em", color: "#9ca3af" }}>
                          b
                        </span>
                        {bNum > 0 && (
                          <span
                            style={{
                              fontSize: "0.9em",
                              color: bInRange ? "#10b981" : "#f59e0b",
                              fontWeight: "bold",
                              padding: "4px 10px",
                              backgroundColor: bInRange
                                ? "rgba(16, 185, 129, 0.1)"
                                : "rgba(245, 158, 11, 0.1)",
                              borderRadius: "6px",
                            }}
                          >
                            = {usdtCost.toFixed(2)} USDT
                          </span>
                        )}
                        <button
                          onClick={() => onSetLiquidity(market.id)}
                          disabled={approvingId === market.id || !bInRange}
                          style={{
                            padding: "8px 16px",
                            borderRadius: "6px",
                            fontWeight: "bold",
                            fontSize: "0.9em",
                            backgroundColor: !bInRange ? "#374151" : "#3b82f6",
                            color: !bInRange ? "#6b7280" : "#fff",
                            border: "none",
                            cursor: !bInRange ? "not-allowed" : "pointer",
                          }}
                        >
                          {approvingId === market.id
                            ? "Setting..."
                            : "Set Liquidity & Approve"}
                        </button>
                      </div>
                      {bNum > 0 && bInRange && (
                        <div
                          style={{
                            fontSize: "0.8em",
                            color: "#6b7280",
                            marginTop: "6px",
                          }}
                        >
                          LMSR cost = {bNum.toFixed(1)} x ln(
                          {market.outcomes_count}) = {usdtCost.toFixed(2)} USDT
                          from pool
                          {usdtCost > platformPoolBalance.available && (
                            <span style={{ color: "#ef4444", marginLeft: "8px" }}>
                              (exceeds pool balance!)
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "0.75em",
                          color: "#6b7280",
                          marginTop: "4px",
                        }}
                      >
                        Pool available:{" "}
                        {platformPoolBalance.available.toLocaleString(undefined, {
                          minimumFractionDigits: 6,
                          maximumFractionDigits: 6,
                        })}{" "}
                        USDT
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      padding: "12px",
                      borderRadius: "6px",
                      backgroundColor: "rgba(239, 68, 68, 0.05)",
                      border: "1px solid rgba(239, 68, 68, 0.2)",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        fontWeight: "bold",
                        marginBottom: "6px",
                        fontSize: "0.9em",
                        color: "#fca5a5",
                      }}
                    >
                      Decline Market
                    </label>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <textarea
                        placeholder="Reason for declining..."
                        value={declineInputs[market.id] || ""}
                        onChange={(event) =>
                          setDeclineInputs((current) => ({
                            ...current,
                            [market.id]: event.target.value,
                          }))
                        }
                        maxLength={500}
                        rows={2}
                        style={{
                          flex: 1,
                          minWidth: "200px",
                          padding: "8px",
                          borderRadius: "6px",
                          backgroundColor: "#1f2937",
                          color: "#e5e7eb",
                          border: "1px solid #374151",
                          resize: "vertical",
                        }}
                      />
                      <button
                        onClick={() => onDeclineMarket(market.id)}
                        disabled={
                          decliningId === market.id ||
                          !(declineInputs[market.id] || "").trim()
                        }
                        style={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontWeight: "bold",
                          fontSize: "0.9em",
                          backgroundColor: !(declineInputs[market.id] || "").trim()
                            ? "#374151"
                            : "#ef4444",
                          color: !(declineInputs[market.id] || "").trim()
                            ? "#6b7280"
                            : "#fff",
                          border: "none",
                          cursor: !(declineInputs[market.id] || "").trim()
                            ? "not-allowed"
                            : "pointer",
                        }}
                      >
                        {decliningId === market.id ? "Declining..." : "Decline"}
                      </button>
                    </div>
                    {(declineInputs[market.id] || "").length > 0 && (
                      <div
                        style={{
                          fontSize: "0.75em",
                          color: "#6b7280",
                          marginTop: "4px",
                          textAlign: "right",
                        }}
                      >
                        {(declineInputs[market.id] || "").length}/500
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </>
      )}
    </div>
  );
};

export default MarketApprovalSection;
